import * as Immutable from 'immutable';

import analytics from '../../../shared/services/analytics/analytics';
import { AlphaTabType } from '../../../shared/constants';
import { AlphaTableType } from '../../../types';
import { TableFilter } from '../../Table/Table';
import { FilterColumn, FilterTrackHandler, FilterValues, SelectOptionsTransform, TableOptions } from '../../Table/tableFilter.types';

// BRAINTECH-5549: the Submitted table only surfaces Active / Decommissioned, so the Unsubmitted
// status choice is dropped there. Only applies to the status column.
const dropUnsubmittedStatus: SelectOptionsTransform = options => options.filter(choice => choice.value !== 'UNSUBMITTED');

const buildAlphaChoicesTransform = (columnId: string, tabType: AlphaTableType): SelectOptionsTransform | undefined => {
  if (columnId !== 'status' || tabType !== AlphaTabType.SUBMITTED) {
    return undefined;
  }
  return dropUnsubmittedStatus;
};

const alphaFilterTrack: FilterTrackHandler = columnId =>
  analytics.trackCustomEvent('alphas_filter_table_column', { event_label: columnId });

// The Redux store holds OPTIONS as an Immutable tree. Convert it once to a plain typed object
// (memoized by the Immutable reference, which is stable across renders when unchanged) so the
// generic TableFilter can resolve columns via plain property access with no `any`.
const tableOptionsCache = new WeakMap<Immutable.Map<string, unknown>, TableOptions>();
const toTableOptions = (alphasOptions: Immutable.Map<string, unknown> | null | undefined): TableOptions | null => {
  if (!alphasOptions) {
    return null;
  }
  let cached = tableOptionsCache.get(alphasOptions);
  if (!cached) {
    cached = alphasOptions.toJS() as unknown as TableOptions;
    tableOptionsCache.set(alphasOptions, cached);
  }
  return cached;
};

export interface AlphaTableFilterParams {
  alphasOptions: Immutable.Map<string, unknown> | null | undefined;
  column: FilterColumn;
  filters: FilterValues;
  filtersUpdated: boolean;
  handleFilterBlur: () => void;
  tabType: AlphaTableType;
}

export const AlphaTableFilter = ({ alphasOptions, column, filters, filtersUpdated, handleFilterBlur, tabType }: AlphaTableFilterParams) =>
  TableFilter({
    tableOptions: toTableOptions(alphasOptions),
    column,
    filters,
    filtersUpdated,
    handleFilterBlur,
    choicesTransform: buildAlphaChoicesTransform(column.id, tabType),
    onFilterTrack: alphaFilterTrack,
  });
